import { useMemo, useState } from 'react';

import { type FieldValues, type UseControllerProps, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { InputGroup, type InputProps } from '@wf/components';

import { type LicenseFieldProps } from './LicenseFieldController';

import { useLicenseController } from '../../hooks/useLicenseController';
import { HeaderTabFields } from '../../types/fieldEnums';

import { defaultScaleSelector } from '@/store/configs/selectors';
import formatNumber from '../../../../helpers/formatNumber';

import styles from './LicenseInputGroupSelect.module.scss';

const enumSelectFields = { [HeaderTabFields.typeOfUse]: HeaderTabFields.typeOfUse };

export interface IInputGroupSelectProps extends InputProps {
  regexp?: RegExp;
  formatAsNumber?: boolean;
  fractionDigits?: number;
  triggerOnChange?: boolean;
  selectVisible?: boolean;
  selectName: string;
  selectConfigName: string;
  options?: SelectOption[];
  defaultSelectValue?: SelectOption | null;
  onSelectChange?: (option: SelectOption | null) => void;
  onSelectBlur?: () => void;
}

export const LicenseInputGroupSelect = ({
  name,
  defaultValue,
  defaultSelectValue,
  regexp,
  configName,
  featureFlag,
  viewMode,
  rules,
  shouldUnregister,
  control,
  formatAsNumber,
  fractionDigits,
  triggerOnChange,
  disabled,
  label,
  selectName,
  selectConfigName,
  selectVisible,
  options,
  placeholder,
  ...rest
}: IInputGroupSelectProps & LicenseFieldProps & UseControllerProps<FieldValues, string>) => {
  const { control: contextControl, trigger } = useFormContext();
  const {
    i18n: { language },
    t,
  } = useTranslation();

  const [focused, setFocused] = useState(false);

  const defaultScale = useSelector(defaultScaleSelector) ?? 2;

  const {
    field: { value, onChange, onBlur, ref: _inputRef, ...fieldRest },
    computedProps,
    config,
  } = useLicenseController({
    name,
    configName,
    featureFlag,
    control: control ?? contextControl,
    defaultValue: defaultValue ?? '',
    rules,
    shouldUnregister,
    viewMode,
    disabled,
  });

  const selectController = useLicenseController({
    name: selectName,
    configName: selectConfigName,
    featureFlag,
    control: control ?? contextControl,
    defaultValue: defaultSelectValue ?? null,
    rules,
    shouldUnregister,
    viewMode,
    disabled,
  });
  const { ref: _, ...selectField } = selectController.field;

  const formattedValue = useMemo(() => {
    if (!formatAsNumber || focused || (!value && value !== 0)) {
      return value ?? '';
    }

    return formatNumber({
      value,
      locale: language,
      maximumFractionDigits: fractionDigits ?? defaultScale,
      minimumFractionDigits: fractionDigits ?? defaultScale,
    });
  }, [focused, formatAsNumber, fractionDigits, language, value, defaultScale]);

  const handleChange: typeof rest.onChange = (e) => {
    if (regexp && !regexp.test(e.target.value) && e.target.value !== '') {
      return;
    }

    onChange(e);

    if ('onChange' in rest && typeof rest.onChange === 'function') {
      rest.onChange(e);
    }

    if (triggerOnChange) {
      void trigger(name);
    }
  };

  const handleFocus: typeof rest.onFocus = (...args) => {
    if ('onFocus' in rest && typeof rest.onFocus === 'function') {
      rest.onFocus(...args);
    }

    setFocused(true);
  };

  const handleBlur: typeof rest.onBlur = (...args) => {
    onBlur();
    setFocused(false);

    if ('onBlur' in rest && typeof rest.onBlur === 'function') {
      rest.onBlur(...args);
    }
  };

  const handleSelectChange: typeof rest.onSelectChange = (...args) => {
    selectField.onChange(...args);

    if ('onSelectChange' in rest && typeof rest.onSelectChange === 'function') {
      rest.onSelectChange(...args);
    }
  };

  const handleSelectBlur: typeof rest.onSelectBlur = (...args) => {
    selectField.onBlur();

    if ('onSelectBlur' in rest && typeof rest.onSelectBlur === 'function') {
      rest.onSelectBlur(...args);
    }
  };

  const getTranslatedValue = () => {
    const value = selectField.value ?? defaultSelectValue;

    if (name in enumSelectFields && value) {
      if (Array.isArray(value)) {
        return value.map((v) => ({
          ...v,
          label: t(v.value),
        }));
      }

      return { ...value, label: t(value.value) };
    }

    if (Array.isArray(value)) {
      return value.map((v) => ({ ...v, label: t(v.value) }));
    }

    return value;
  };

  if (config.isHidden) {
    return <div></div>;
  }

  const formattedValueView = (
    <>
      {(formattedValue as string) || '-'}{' '}
      <span className={styles.valueView}>{(getTranslatedValue()?.value as string) || ''}</span>
    </>
  );

  const RenderSelectField = () => {
    if (!selectVisible) {
      return <></>;
    }

    if (options && options.length < 1) {
      return <div className={styles.selectValue}>{getTranslatedValue()?.value}</div>;
    }

    return (
      <InputGroup.Select
        className={styles.selectField}
        options={options}
        {...selectController.computedProps}
        {...selectField}
        valueView={getTranslatedValue() || 'select'}
        onChange={handleSelectChange}
        value={getTranslatedValue()}
        onBlur={handleSelectBlur}
        isSearchable={false}
        hasError={false}
        errorMessage=""
      />
    );
  };

  return (
    <div className={styles.positionRelative}>
      <InputGroup
        {...computedProps}
        hasError={computedProps.hasError || selectController.computedProps.hasError}
        valueView={formattedValueView}
        viewMode={viewMode}
        errorMessage=""
        label={label}
      >
        <InputGroup.Input
          {...computedProps}
          {...fieldRest}
          valueView={formattedValue || '-'}
          placeholder={placeholder}
          onChange={handleChange}
          value={formattedValue}
          onFocus={handleFocus}
          onBlur={handleBlur}
          autoComplete="off"
          hasError={false}
          errorMessage=""
        />
        <RenderSelectField />
      </InputGroup>
      <div className={styles.fieldError}>
        {computedProps.hasError && <span>{t(computedProps.errorMessage) + ' '}</span>}
        {selectController.computedProps.hasError && <span>{t(selectController.computedProps.errorMessage)}</span>}
      </div>
    </div>
  );
};
